import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { Loader2 } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Button — shadcn variants + pastel fills for admin top actions.
 * Uses `transition-interactive` for smooth GPU-accelerated hover/active feedback.
 *
 * Action → variant (do not invent colors): see docs/DESIGN_SYSTEM.md “Button actions”
 * and .cursor/rules/28-button-action-variants.mdc
 *   default     = primary commit / advance (Save, Next, Submit, Create)
 *   outline     = secondary / dismiss / reverse (Cancel, Back, Replace)
 *   ghost       = quiet tertiary
 *   destructive = delete / permanent reject
 *   success     = explicit approve (rare)
 *   warning     = risky non-delete (rare)
 *   export      = CSV / spreadsheet export
 *   link        = inline text action
 *   pastel-*    = admin top-bar soft actions only
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-interactive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98] motion-reduce:active:scale-100",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover active:bg-primary-active hover:-translate-y-px hover:shadow-md motion-reduce:hover:translate-y-0",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 hover:-translate-y-px hover:shadow-md motion-reduce:hover:translate-y-0",
        outline:
          "border border-field-border bg-field hover:bg-hover hover:text-foreground hover:border-border-strong hover:-translate-y-px hover:shadow-sm motion-reduce:hover:translate-y-0",
        /** Excel / Export CSV — outline at rest, Add/Create blue fill on hover */
        export:
          "border border-field-border bg-field text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary hover:-translate-y-px hover:shadow-md motion-reduce:hover:translate-y-0",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
        ghost:
          "hover:bg-hover hover:text-foreground hover:-translate-y-px motion-reduce:hover:translate-y-0",
        link: "text-link underline-offset-4 hover:text-link-hover hover:underline active:scale-100",
        success:
          "bg-success text-success-foreground shadow-sm hover:bg-success/90 hover:-translate-y-px hover:shadow-md motion-reduce:hover:translate-y-0",
        warning:
          "bg-warning text-warning-foreground shadow-sm hover:bg-warning/90 hover:-translate-y-px hover:shadow-md motion-reduce:hover:translate-y-0",
        "pastel-blue":
          "bg-pastel-blue text-pastel-blue-fg hover:bg-pastel-blue/70 hover:-translate-y-px hover:shadow-soft motion-reduce:hover:translate-y-0",
        "pastel-mint":
          "bg-pastel-mint text-pastel-mint-fg hover:bg-pastel-mint/70 hover:-translate-y-px hover:shadow-soft motion-reduce:hover:translate-y-0",
        "pastel-pink":
          "bg-pastel-pink text-pastel-pink-fg hover:bg-pastel-pink/70 hover:-translate-y-px hover:shadow-soft motion-reduce:hover:translate-y-0",
        "pastel-lavender":
          "bg-pastel-lavender text-pastel-lavender-fg hover:bg-pastel-lavender/70 hover:-translate-y-px hover:shadow-soft motion-reduce:hover:translate-y-0",
        "pastel-amber":
          "bg-pastel-amber text-pastel-amber-fg hover:bg-pastel-amber/70 hover:-translate-y-px hover:shadow-soft motion-reduce:hover:translate-y-0",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner and disables interaction while true. */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    // Slot requires exactly one element child — never inject the loader beside it.
    const content = asChild ? (
      children
    ) : (
      <>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
        {children}
      </>
    );
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components -- shadcn pattern: variants co-located with component
export { Button, buttonVariants };
